import { CssBaseline, GlobalStyles, InputBase, styled } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import designLabLogoUrl from '@resources/img/create/design-lab-v4/logo-design-lab.svg?url';
import type ViewProductStore from '../../AppData/_stores/ViewProductStore';
import SessionMismatchDialog from '../../Auth/SessionMismatchDialog';
import CookieConsent from '../../Header/CookieConsent';
import LiveChatAiMessenger from '../../Header/LiveChat/LiveChatAiMessenger';
import UserNav from '../../Header/UserNav';
import Toast from '../../UIData/_components/Toast';
import LabDevTool from '../DevTool/LabDevTool';
import LabDevTexturesProvider from '../DevTool/LabDevTextures';
import ProductSelector from '../ProductSelector/ProductSelector';
import ExitLab from './ExitLab';
import LabInterface from './LabInterface';
import VariantSelector from './VariantSelector';

const DesignLabAppContainer = styled('div')(({ theme }) => ({
  '--design-lab-content-top': '74px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 1000,
  background: '#F3F3F3',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  [theme.breakpoints.down('md')]: {
    '--design-lab-content-top': '62px',

    '&.has-mobile-selector-bar': {
      '--design-lab-content-top': '102px',
    },
  },
}))

export const InputSelect = styled(InputBase)({
  height: '100%',
  color: '#fff',

  '.MuiInputBase-input:focus': {
    background: 'transparent',
  },

  '.MuiSelect-select': {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '0 34px 0 0',
  },

  '.MuiSelect-icon': {
    color: '#fff',
  }
})

const YellowDecoration = styled('div')({
  overflow: 'hidden',
  maxWidth: '80px',
  maxHeight: '120px',
  position: 'absolute',
  zIndex: -1,
  right: 0,
  bottom: 0,

  '&:before': {
    content: '""',
    height: '150px',
    width: '150px',
    border: '20px solid #FCF57F',
    display: 'block',
    transform: 'rotate(-20deg)',
    marginLeft: '30px',
  }
})

const PinkDecoration = styled('div')({
  overflow: 'hidden',
  maxWidth: '80px',
  maxHeight: '160px',
  position: 'absolute',
  zIndex: -1,

  '&:before': {
    content: '""',
    height: '150px',
    width: '60px',
    backgroundColor: '#F396D6',
    display: 'block',
    transform: 'rotate(25deg)',
    marginLeft: 0,
  },

  '&.right': {
    right: 0,
    bottom: 0,
    top: '25vh',

    '&:before': {
      margin: '0 -40px 30px 30px',
    },
  },

  '&.left': {
    left: 0,
    bottom: 0,

    '&:before': {
      margin: '30px 30px 30px -40px',
    }
  }
})

const LabHeader = styled('div')(({ theme }) => ({
  alignItems: 'stretch',
  background: '#000',
  color: '#fff',
  display: 'grid',
  flexShrink: 0,
  gridTemplateColumns: 'auto minmax(0, 1fr) auto',
  gridTemplateRows: '64px',
  height: 64,
  marginBottom: 10,
  zIndex: 5,

  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: 'minmax(0, 1fr) auto',
    gridTemplateRows: '52px',
    height: 52,

    '&.with-mobile-selector-bar': {
      gridTemplateRows: '52px 40px',
      height: 92,
    },
  },
}))

const HeaderBrand = styled('div')({
  alignItems: 'center',
  display: 'flex',
  gridColumn: '1 / 2',
  gridRow: '1',
  minWidth: 0,
})

const LogoWrapper = styled('h1')(({ theme }) => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
  fontSize: 0,
  margin: theme.spacing(0, 3, 0, 2),
  height: '40px',
  lineHeight: 0,
  overflow: 'hidden',
  width: 'auto',

  [theme.breakpoints.down('md')]: {
    height: '32px',
    margin: theme.spacing(0, 2),
  },
}))

const HeaderProductControls = styled('div')(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  gridColumn: '2 / 3',
  gridRow: '1',
  minWidth: 0,
  overflow: 'visible',
  paddingLeft: theme.spacing(1),
  position: 'relative',

  [theme.breakpoints.down('md')]: {
    alignItems: 'center',
    borderTop: '1px solid rgba(255, 255, 255, 0.18)',
    gridColumn: '1 / -1',
    gridRow: '2',
    height: 40,
    padding: 0,
  },
}))

const HeaderActions = styled('div')(({ theme }) => ({
  alignItems: 'stretch',
  display: 'flex',
  gridColumn: '3 / 4',
  gridRow: '1',
  justifySelf: 'end',
  minWidth: 0,

  [theme.breakpoints.down('md')]: {
    gridColumn: '2 / 3',
    height: 52,
  },
}))

const HeaderActionSeparator = styled('div')(({ theme }) => ({
  alignSelf: 'center',
  background: 'rgba(255, 255, 255, 0.35)',
  flexShrink: 0,
  height: 36,
  width: 1,

  [theme.breakpoints.down('md')]: {
    height: 28,
  },
}))

export type SaveState = 'none' | 'generating' | 'create_more' | 'done' | 'error';

const zendeskWidgetSelector = [
  'iframe[src*="zdassets.com"]',
  'iframe[src*="zendesk.com"]',
  'iframe[title*="Zendesk"]',
  'iframe[aria-label*="Zendesk"]',
  'iframe[title*="Messaging"]',
  'iframe[aria-label*="Messaging"]',
  'iframe[title*="messaging"]',
  'iframe[aria-label*="messaging"]',
].join(',')

export default function DesignLab() {
  const [searchParams] = useSearchParams();
  const [selectedViewProduct, setSelectedViewProduct] = useState<ViewProductStore | undefined>(undefined);
  const [previewVariantByProductId, setPreviewVariantByProductId] = useState<Record<number, string>>({});

  const isEditing = useMemo(() => {
    return searchParams.has('design');
  }, [searchParams]);

  const onPreviewVariantChange = useCallback((productId: number, variant: string) => {
    setPreviewVariantByProductId(value => ({
      ...value,
      [productId]: variant,
    }));
  }, [])

  return (
    <LabDevTexturesProvider><CssBaseline>
      <GlobalStyles
        styles={(theme) => ({
          html: {
            overscrollBehavior: 'none',
            overflow: 'hidden',
          },
          [zendeskWidgetSelector]: {
            left: '24px !important',
            right: 'auto !important',
          },
          ['@media (max-width: ' + theme.breakpoints.values.lab_mobile + 'px)']: {
            [zendeskWidgetSelector]: {
              display: 'none !important',
            },
          }
        })}
      />

      <DesignLabAppContainer className={!isEditing ? 'has-mobile-selector-bar' : undefined}>
        <YellowDecoration />
        <PinkDecoration className="left" />
        <PinkDecoration className="right" />
        <LabHeader className={!isEditing ? 'with-mobile-selector-bar' : undefined}>
          <HeaderBrand>
            <LogoWrapper>
              <img src={designLabLogoUrl} alt="Design Lab" style={{ width: 'auto', height: '100%' }} />
            </LogoWrapper>
            <HeaderActionSeparator
              aria-hidden
              sx={{
                display: {
                  xs: 'none',
                  md: 'block',
                }
              }}
            />
          </HeaderBrand>

          {!isEditing ? <HeaderProductControls>
            <ProductSelector
              previewVariantByProductId={previewVariantByProductId}
              onPreviewVariantChange={onPreviewVariantChange}
              onSelectedViewProductChange={setSelectedViewProduct}
            />
            <VariantSelector
              previewVariantByProductId={previewVariantByProductId}
              selectedViewProduct={selectedViewProduct}
              onPreviewVariantChange={onPreviewVariantChange}
            />
          </HeaderProductControls> : null}

          <HeaderActions>
            <LabDevTool />
            <UserNav hideCreateButton />
            <HeaderActionSeparator aria-hidden />
            <ExitLab />
          </HeaderActions>
        </LabHeader>

        <LabInterface selectedViewProduct={selectedViewProduct} />
      </DesignLabAppContainer>
      <CookieConsent />
      <LiveChatAiMessenger side="left" vertical="bottom" hideOnLabMobile />

      <Toast />
      <SessionMismatchDialog />
    </CssBaseline></LabDevTexturesProvider>
  );
}
